import { useFetch } from "@/lib/content";
import { useSEO } from "@/lib/seo";
import { Eyebrow, Reveal, CtaButton } from "@/components/site/Motion";
import { TestimonialCard } from "@/components/site/Testimonials";

export default function TestimonialsPage() {
  const { data: items } = useFetch("/testimonials");
  const list = items || [];

  useSEO({
    title: "Client Testimonials | RenB Solutions",
    description:
      "What founders and CEOs say about working with RenB Solutions — growth, transformation, strategy and leadership delivered hands-on inside SMEs across India.",
    path: "/testimonials",
  });

  return (
    <div data-testid="testimonials-page">
      <section className="border-b border-ash">
        <div className="mx-auto grid max-w-[1440px] gap-12 px-5 pb-16 pt-28 md:px-10 md:pb-20 md:pt-32 lg:grid-cols-12">
          <Reveal className="lg:col-span-8">
            <Eyebrow>What clients say</Eyebrow>
            <h1 className="mt-8 max-w-4xl font-serif text-5xl font-medium leading-[1.02] tracking-tight md:text-7xl">
              In their words<span className="text-progress">.</span>
            </h1>
            <p className="mt-8 max-w-2xl text-base leading-relaxed text-obsidian/70 md:text-lg">
              Founders and CEOs on what changed when RenB came inside their business — in their own words, unedited.
            </p>
          </Reveal>
          <Reveal delay={0.1} className="flex items-end lg:col-span-3 lg:col-start-10">
            <p data-testid="testimonials-count" className="text-[13px] font-bold uppercase tracking-[0.2em] text-slategray">
              {list.length > 0 ? `${list.length} testimonials` : ""}
            </p>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-5 py-16 md:px-10 md:py-24">
        {items && list.length === 0 && (
          <p data-testid="testimonials-empty" className="py-24 text-center text-slategray">
            Testimonials will appear here as they are published.
          </p>
        )}
        {list.length > 0 && (
          <div data-testid="testimonials-grid" className="grid gap-px border border-ash/40 bg-ash/40 md:grid-cols-2 lg:grid-cols-3">
            {list.map((t, i) => (
              <Reveal key={t.id} delay={Math.min(i % 3, 2) * 0.06} className="flex flex-col bg-paper p-8 md:p-10">
                <TestimonialCard t={t} />
              </Reveal>
            ))}
          </div>
        )}
      </section>

      <section className="border-t border-ash bg-obsidian text-paper">
        <div className="mx-auto grid max-w-[1440px] gap-10 px-5 py-20 md:grid-cols-12 md:px-10 md:py-28">
          <div className="md:col-span-7">
            <Eyebrow light>Your turn</Eyebrow>
            <h2 className="mt-6 font-serif text-3xl font-medium leading-tight tracking-tight md:text-5xl">
              Tell us where it hurts.
            </h2>
            <p className="mt-6 max-w-xl text-base leading-relaxed text-paper/70">
              A 15-minute discovery call. We&apos;ll tell you what we&apos;d fix first — and whether we&apos;re the right partner for it.
            </p>
          </div>
          <div className="flex items-end md:col-span-4 md:col-start-9 md:justify-end">
            <CtaButton href="/book" testid="testimonials-cta">
              Book a Discovery Call
            </CtaButton>
          </div>
        </div>
      </section>
    </div>
  );
}
